import { t } from "@lingui/macro";
import {
  ChatCircle,
  Gear,
  Lightning,
  List,
  ReadCvLogo,
  SignOut,
  X,
} from "@phosphor-icons/react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@reactive-resume/ui";
import { motion } from "framer-motion";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Logo } from "@/client/components/logo";
import { ThemeSwitch } from "@/client/components/theme-switch";
import { UserAvatar } from "@/client/components/user-avatar";
import { useLogout } from "@/client/services/auth";
import { useUser } from "@/client/services/user";
import { useAuthStore } from "@/client/stores/auth";

const NAV_LINKS = [
  { label: "Features", href: "/#ai-writer" },
  { label: "Templates", href: "/#templates" },
  { label: "Pricing", href: "/#pricing" },
  { label: "Blog", href: "/blog/", external: true },
  { label: "Contact", href: "/contact" },
];

const PromoBanner = () => (
  <div className="bg-primary px-4 py-2 text-center text-xs font-medium text-primary-foreground sm:text-sm">
    <a href="/#pricing" className="hover:underline">
      <Lightning weight="fill" className="mr-1 inline-block h-3 w-3 sm:h-3.5 sm:w-3.5" />
      {t`Smarter AI just dropped: writing, ATS scoring & job matching. 3-day free trial, save up to 5 months annually`}
      <span className="ml-1">→</span>
    </a>
  </div>
);

export const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const isLoggedIn = useAuthStore((state) => !!state.user);
  const { user } = useUser();
  const { logout } = useLogout();
  const navigate = useNavigate();

  return (
    <>
      <PromoBanner />

      <motion.header
        className="sticky top-0 z-20 border-b border-border/40 bg-background/80 backdrop-blur-md"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0, transition: { delay: 0.1, duration: 0.3 } }}
      >
        <div className="container flex h-14 min-w-0 items-center justify-between">
          <Link to="/" className="flex shrink-0 items-center gap-2">
            <Logo size={28} />
            <span className="text-base font-semibold tracking-tight">{t`CareerBldr`}</span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {NAV_LINKS.map((link) =>
              link.external ? (
                <a
                  key={link.href}
                  href={link.href}
                  className="rounded-sm px-3 py-2 text-sm font-medium text-foreground/60 transition-colors hover:text-foreground"
                >
                  {link.label}
                </a>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  className="rounded-sm px-3 py-2 text-sm font-medium text-foreground/60 transition-colors hover:text-foreground"
                >
                  {link.label}
                </a>
              ),
            )}
          </nav>

          <div className="flex items-center gap-2">
            <ThemeSwitch />

            {isLoggedIn ? (
              <>
                <Button asChild size="sm" className="hidden sm:inline-flex">
                  <Link to="/dashboard">{t`Dashboard`}</Link>
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="shrink-0 rounded-full ring-2 ring-border transition-all hover:ring-primary/50 focus-visible:outline-none focus-visible:ring-primary/50">
                      <UserAvatar size={30} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent side="bottom" align="end" className="w-48">
                    {user?.name && (
                      <>
                        <div className="px-2.5 py-2">
                          <p className="truncate text-sm font-medium">{user.name}</p>
                          <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                        </div>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem onClick={() => navigate("/dashboard/resumes")}>
                      <ReadCvLogo size={14} />
                      <span className="ml-2">{t`Resumes`}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/dashboard/settings")}>
                      <Gear size={14} />
                      <span className="ml-2">{t`Settings`}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/contact")}>
                      <ChatCircle size={14} />
                      <span className="ml-2">{t`Contact Us`}</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => logout()}>
                      <SignOut size={14} />
                      <span className="ml-2">{t`Logout`}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="hidden items-center gap-1 sm:flex">
                <Button asChild variant="ghost" size="sm">
                  <Link to="/auth/login">{t`Log in`}</Link>
                </Button>
                <Button asChild size="sm">
                  <Link to="/auth/register">{t`Register`}</Link>
                </Button>
              </div>
            )}

            <Button
              size="icon"
              variant="ghost"
              className="md:hidden"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X size={20} /> : <List size={20} />}
            </Button>
          </div>
        </div>

        {mobileOpen && (
          <div className="border-t border-border/40 bg-background md:hidden">
            <nav className="container space-y-1 py-4">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="block rounded-sm px-3 py-2.5 text-sm font-medium text-foreground/70 transition-colors hover:bg-secondary hover:text-foreground"
                >
                  {link.label}
                </a>
              ))}
              <div className="border-t border-border/30 pt-3">
                {isLoggedIn ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 px-3 py-2">
                      <UserAvatar size={32} />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{user?.name}</p>
                        <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
                      </div>
                    </div>
                    <Button asChild size="sm" className="w-full">
                      <Link to="/dashboard" onClick={() => setMobileOpen(false)}>
                        {t`Go to Dashboard`}
                      </Link>
                    </Button>
                    <div className="flex gap-2">
                      <Button
                        asChild
                        size="sm"
                        variant="outline"
                        className="flex-1"
                      >
                        <Link to="/dashboard/settings" onClick={() => setMobileOpen(false)}>
                          <Gear size={14} className="mr-1.5" />
                          {t`Settings`}
                        </Link>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => { logout(); setMobileOpen(false); }}
                      >
                        <SignOut size={14} className="mr-1.5" />
                        {t`Logout`}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Button asChild size="sm" className="w-full">
                      <Link to="/auth/register" onClick={() => setMobileOpen(false)}>
                        {t`Register`}
                      </Link>
                    </Button>
                    <Button asChild variant="outline" size="sm" className="w-full">
                      <Link to="/auth/login" onClick={() => setMobileOpen(false)}>
                        {t`Log in`}
                      </Link>
                    </Button>
                  </div>
                )}
              </div>
            </nav>
          </div>
        )}
      </motion.header>
    </>
  );
};
